// Ported from dev-scrips-pm-react src/billing/services/use-invoice-services.ts (PROD-3312 appointment Billing tab).
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { EMPTY_GUID, isEmptyGuid } from '@/shared/lib/empty-guid';
import type { CodeDisplayName } from '@/shared/types/billing';

/**
 * Service catalog for the appointment / patient billing tab "+" Add-service
 * picker (PROD-1006). Curl-verified 2026-06-04:
 *
 *       POST /api/Billing/Services/Fees?pageNumber&pageSize
 *       body { providerId, locationId, patientId, type:"provider" }
 *       → { results: [{ id, procedureCode, shortDescription, fees:[{id,fee,location}] }] }
 *
 * NOTE: the invoice recalc/persist path is NOT here — add/remove/qty flows
 * through `useAddRemoveService` (PUT /Billing/Invoice/{id}); the former
 * `useUpdateInvoicePricing` (a PostBookCalculatePrice wrapper) was dead code
 * with zero callers and was removed (PROD-1809 review, PR #809).
 */

export interface ServiceFeeLocation {
  id?: string;
  name?: string;
  description?: string | null;
}
export interface ServiceFee {
  id?: string;
  fee?: number;
  location?: ServiceFeeLocation;
}
export interface ServiceFeeResult {
  id?: string;
  procedureCode?: CodeDisplayName;
  shortDescription?: string;
  fees?: ServiceFee[];
}
interface ServiceFeesResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ServiceFeeResult[];
}

/** "provider" = only this provider's fee-schedule services; "all" = the full
 *  org catalog (curl-verified 2026-06-04 — the picker toggle). */
export type ServiceFeesType = 'provider' | 'all';

export interface ServiceFeesParams {
  providerId?: string;
  locationId?: string;
  patientId?: string;
  type?: ServiceFeesType;
  /** Free-text search. Sent to the backend as the `q` query param (Flutter
   *  `fetchServicesFees` — `billing_data_source_impl.dart:430-432`), NOT a
   *  client-side filter, so the results reflect the full catalog match. */
  query?: string;
}

/**
 * Is this parameter set one the backend can actually answer?
 *
 * PROD-2368. The old gate was `Boolean(providerId && patientId)` — a provider
 * was required for BOTH tabs, which is wrong in both directions on a
 * provider-less invoice (a device booking, PROD-2007):
 *
 *   - `type: 'all'` does not need a provider at all. Requiring one meant the
 *     one tab that CAN answer never fired, and the dialog rendered "No services
 *     found" for a request it had chosen not to send.
 *   - `type: 'provider'` needs a REAL provider. `Guid.Empty` is truthy as a
 *     string, so it sailed through the old gate and the server rejected it:
 *
 *         400 {"errors":{"ProviderId":
 *              ["ProviderId cannot be empty when Type is Provider"]}}
 *
 * Exported so the picker can tell "zero rows" from "no request was made"
 * without re-deriving the rule and drifting from it.
 */
export function canFetchServiceFees({
  providerId,
  patientId,
  type = 'provider',
}: ServiceFeesParams): boolean {
  if (!patientId) return false;
  if (type === 'provider' && isEmptyGuid(providerId)) return false;
  return true;
}

/**
 * Catalog of services the provider can bill (the Add-service picker). `type`
 * toggles Provider Fees vs All; `query` searches server-side via `&q=`. Fetched
 * lazily — pass `enabled` (e.g. when the picker opens) so it doesn't fire on
 * every billing-tab mount. `keepPreviousData` holds the last results visible
 * while a new search settles (no flash of empty on each keystroke).
 */
export function useServiceFees(params: ServiceFeesParams, enabled = true) {
  const { providerId, locationId, patientId, type = 'provider', query } = params;
  const q = query?.trim() || undefined;
  // `''` and `undefined` mean the same thing here — no provider — but the .NET
  // side binds a non-nullable `Guid providerId` and rejects `''` outright while
  // binding `Guid.Empty` fine (see `shared/lib/empty-guid.ts`). Normalising
  // once also collapses the two spellings onto ONE query key, so the same
  // provider-less invoice does not get two cache entries depending on which of
  // `invoice.providerId` / `invoice.provider?.id` the caller happened to find.
  const provider = providerId || EMPTY_GUID;
  return useQuery({
    queryKey: ['billing', 'service-fees', provider, locationId, patientId, type, q],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ pageNumber: '1', pageSize: '50' });
      if (q) qs.set('q', q);
      return post<ServiceFeesResponse>(
        `/api/Billing/Services/Fees?${qs.toString()}`,
        { providerId: provider, locationId, patientId, type },
        { signal },
      );
    },
    enabled: enabled && canFetchServiceFees({ providerId, patientId, type }),
    placeholderData: keepPreviousData,
    // No staleTime, and refetch on every mount.
    //
    // This used to be `staleTime: 5 * 60 * 1000`, which meant reopening the
    // Add-service dialog inside five minutes rendered the CACHED list and made
    // no request at all. Reported by Andrew 2026-08-25 as the picker not
    // calling the backend when opened.
    //
    // A cached fee list is the wrong trade here: fees, provider assignments
    // and the patient's eligibility can all change between opening the dialog
    // twice, and the second open is exactly when someone is re-checking
    // because something looked wrong. `keepPreviousData` above means the
    // refetch is not visually disruptive — the previous rows stay up while the
    // fresh ones load.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

